import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Container,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import clsx from 'clsx'
import React, { useState } from 'react'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { withRouter } from 'react-router-dom/cjs/react-router-dom'
import { Translate, withLocalize } from 'react-localize-redux'
import flagUS from '../../../assets/images/usa.svg'
import flagEU from '../../../assets/images/eu.svg'
import SubscriptionPlansData from '../../../SubscriptionPlansData'
import useStyles from './styles'
import SubscriptionPlanActionButton from './SubscriptionPlanActionButton'

const SubscriptionPlansTable = ({
  locale,
  subscription,
  onSubscribe,
  onUpdate,
  isLoading,
  userCurrency,
}) => {
  const classes = useStyles()

  const [interval, setInterval] = useState('yearly')
  const [currency, setCurrency] = useState(userCurrency || 'usd')

  const showCurrencySwitcher = !subscription && !userCurrency

  const handleIntervalSelection = (value) => {
    setInterval(value)
  }

  const handleCurrencyChange = (event) => {
    setCurrency(event.target.value)
  }

  const formatPrice = (price) => {
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
    })

    return formatter.format(price)
  }

  return (
    <Container fixed className={classes.root}>
      <Box textAlign="center" className={classes.headingBox} mb={3} mt={16}>
        <Box mb={2}>
          <Typography variant="h1">
            <Translate id="plans.pick-a-plan" />
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1">
            <Translate id="plans.choose-between-a-monthly-or-yearly" />
          </Typography>
        </Box>
      </Box>

      <Grid
        container
        id="manage-plan-view"
        justifyContent="center"
        alignItems="center"
      >
        <Grid item xs={showCurrencySwitcher ? 11 : 12}>
          <Box textAlign="center">
            <ButtonGroup
              className={classes.toggleButtonGroup}
              color="primary"
              size="large"
              disabled={isLoading}
            >
              {['yearly', 'monthly'].map((value) => (
                <Button
                  key={value}
                  onClick={() => handleIntervalSelection(value)}
                  className={clsx({
                    [classes.selectedPlanInterval]: interval === value,
                    [classes.noHover]: interval === value,
                  })}
                  size="small"
                >
                  <Translate id={`plans.${value}`} />
                </Button>
              ))}
            </ButtonGroup>
          </Box>
        </Grid>
        {showCurrencySwitcher && (
          <Grid item xs={1}>
            <FormControl variant="outlined" size="small">
              <Select
                value={currency}
                onChange={handleCurrencyChange}
                displayEmpty
                className={classes.currencySelect}
                disabled={isLoading}
                MenuProps={{
                  classes: { paper: classes.customMenuPaper },
                  elevation: 1,
                }}
              >
                <MenuItem value="usd" className={classes.menuItem}>
                  <Box className={classes.roundedContainer}>
                    <img
                      src={flagUS}
                      alt="USA Flag"
                      className={classes.containedImage}
                    />
                  </Box>
                  <Typography>
                    <strong>USD</strong>
                  </Typography>
                </MenuItem>
                <MenuItem value="eur" className={classes.menuItem}>
                  <Box className={classes.roundedContainer}>
                    <img
                      src={flagEU}
                      alt="EU Flag"
                      className={classes.containedImage}
                    />
                  </Box>
                  <Typography>
                    <strong>EUR</strong>
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      <Box mt={6}>
        <TableContainer style={{ overflowX: 'unset' }}>
          <Table
            className={classes.table}
            size="small"
            style={{ borderCollapse: 'unset' }}
          >
            <TableHead>
              <TableRow>
                <TableCell className={classes.verticalBottomAlign}>
                  <Typography variant="h3">
                    <Translate id="plans.what-you-wll-get" />
                  </Typography>
                </TableCell>
                {Object.entries(SubscriptionPlansData.plans).map(
                  ([key, plan]) => {
                    const priceKey = `${key}_${interval}_${currency}`
                    const priceData = SubscriptionPlansData.prices[priceKey]

                    if (!priceData) {
                      return <TableCell key={key} align="center" />
                    }

                    const isCurrent =
                      !!subscription &&
                      priceData.plan_uuid ===
                        subscription.subscription_plan_uuid
                    const isPromoted = plan.promoted

                    return (
                      <TableCell
                        key={key}
                        align="center"
                        className={clsx(
                          classes.verticalBottomAlign,
                          classes.promotedCell,
                          {
                            [classes.paddingTop]: !isCurrent,
                            [classes.promotedColumnTop]: isPromoted,
                          }
                        )}
                      >
                        {/* This is recommended plan tag */}
                        {isPromoted && (
                          <Chip
                            className={classes.recommendedTag}
                            label={<Translate id="plans.recommended" />}
                            color="primary"
                            variant="outlined"
                          />
                        )}

                        <Typography variant="h3">{plan.title}</Typography>
                        <Typography variant="h1" className={classes.pricesTyo}>
                          {priceData.amount > 0 ? (
                            <>
                              {formatPrice(priceData.amount)}
                              <Typography variant="body1">
                                /&nbsp;
                                <Translate id={`plans.${interval}`} />
                              </Typography>
                            </>
                          ) : (
                            'Free'
                          )}
                        </Typography>

                        <SubscriptionPlanActionButton
                          isCurrent={isCurrent}
                          hasSubscription={!!subscription}
                          onSubscribe={() =>
                            onSubscribe(priceData.plan_uuid, priceData.amount)
                          }
                          onUpdate={() =>
                            onUpdate(subscription, priceData.plan_uuid)
                          }
                          disabled={isLoading}
                        />
                      </TableCell>
                    )
                  }
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {/* Free Trial row  */}
              {!subscription && (
                <TableRow>
                  <TableCell className={classes.featurelabel}>
                    <Translate id="plans.free-trial" />
                  </TableCell>
                  {Object.entries(SubscriptionPlansData.plans).map(
                    ([key, plan]) => {
                      const priceKey = `${key}_${interval}_${currency}`
                      const priceData = SubscriptionPlansData.prices[priceKey]
                      const isPromoted = plan.promoted
                      const trialDays = plan.trial_days

                      return (
                        <TableCell
                          key={key}
                          align="center"
                          className={clsx({
                            [classes.promotedColumn]: isPromoted && priceData,
                          })}
                        >
                          {!priceData && <Box />}
                          {priceData && (
                            <Typography
                              className={classes.trialDaysText}
                              color="primary"
                            >
                              {trialDays > 0 && (
                                <>
                                  {trialDays} <Translate id="home.days" />
                                </>
                              )}
                              {trialDays <= 0 && '—'}
                            </Typography>
                          )}
                        </TableCell>
                      )
                    }
                  )}
                </TableRow>
              )}

              {/* Features rows */}
              {SubscriptionPlansData.features.map((feature, index) => {
                const isLast =
                  index === SubscriptionPlansData.features.length - 1

                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell className={classes.featurelabel}>
                      {feature.label[locale] || feature.label.en}
                    </TableCell>
                    {Object.entries(SubscriptionPlansData.plans).map(
                      ([key, plan]) => {
                        const isFeatureEnabled = feature[key]
                        const priceKey = `${key}_${interval}_${currency}`
                        const priceData = SubscriptionPlansData.prices[priceKey]
                        const isPromoted = plan.promoted

                        return (
                          <TableCell
                            key={key}
                            align="center"
                            className={clsx({
                              [classes.promotedColumnBottom]:
                                isLast && isPromoted && priceData,
                              [classes.promotedColumn]:
                                !isLast && isPromoted && priceData,
                            })}
                          >
                            {!priceData && <Box />}
                            {priceData && isFeatureEnabled && (
                              <CheckCircleIcon
                                color="primary"
                                fontSize="small"
                              />
                            )}
                            {priceData && !isFeatureEnabled && (
                              <Box className={classes.roundBox} />
                            )}
                          </TableCell>
                        )
                      }
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  )
}

SubscriptionPlansTable.defaultProps = {
  subscription: null,
  userCurrency: null,
}

SubscriptionPlansTable.propTypes = {
  locale: PropTypes.string.isRequired,
  subscription: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    subscription_plan_uuid: PropTypes.string.isRequired,
  }),
  isLoading: PropTypes.bool.isRequired,
  onSubscribe: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  userCurrency: PropTypes.string,
}

export default compose(
  withRouter,
  withLocalize,
  connect(({ auth: { locale } }) => ({
    locale,
  }))
)(SubscriptionPlansTable)
